/**
 * Address Validation Helpers
 *
 * Utilities for validating and determining how to display addresses
 * based on coordinate values and resolved address data.
 */

export interface AddressDisplayInfo {
  shouldShow: boolean;
  isHidden: boolean;
  isUnknown: boolean;
  isNA: boolean;
}

/**
 * Determines how an address should be displayed based on coordinate values and resolved address.
 *
 * @param latitude - The latitude coordinate (can be null, undefined, or a number)
 * @param longitude - The longitude coordinate (can be null, undefined, or a number)
 * @param address - The resolved address string (can be null, undefined, or a string)
 * @returns An object indicating how the address should be displayed
 *
 * @example
 * // Address is hidden (coordinates are null)
 * getAddressDisplayInfo(null, null, null)
 * // Returns: { shouldShow: false, isHidden: true, isUnknown: false, isNA: false }
 *
 * @example
 * // Address is unknown (coordinates are 0)
 * getAddressDisplayInfo(0, 0, null)
 * // Returns: { shouldShow: false, isHidden: false, isUnknown: true, isNA: false }
 *
 * @example
 * // Address resolved as "N/A"
 * getAddressDisplayInfo(51.922549, 4.237252, "N/A")
 * // Returns: { shouldShow: false, isHidden: false, isUnknown: false, isNA: true }
 *
 * @example
 * // Valid address
 * getAddressDisplayInfo(51.922549, 4.237252, "123 Main St, City")
 * // Returns: { shouldShow: true, isHidden: false, isUnknown: false, isNA: false }
 */
export const getAddressDisplayInfo = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  address: string | null | undefined
): AddressDisplayInfo => {
  // If coordinates are null or undefined, show "Address is hidden"
  if (
    latitude === null ||
    longitude === null ||
    latitude === undefined ||
    longitude === undefined
  ) {
    return {
      shouldShow: false,
      isHidden: true,
      isUnknown: false,
      isNA: false
    };
  }

  // If coordinates are 0, show "Unknown"
  if (latitude === 0 || longitude === 0) {
    return {
      shouldShow: false,
      isHidden: false,
      isUnknown: true,
      isNA: false
    };
  }

  // If coordinates are valid but address is "N/A", show "N/A" badge
  if (address === "N/A") {
    return {
      shouldShow: false,
      isHidden: false,
      isUnknown: false,
      isNA: true
    };
  }

  // Otherwise, show the address
  return {
    shouldShow: true,
    isHidden: false,
    isUnknown: false,
    isNA: false
  };
};

/**
 * Helper to check if coordinates are valid (not null, undefined, or 0)
 *
 * @param latitude - The latitude coordinate
 * @param longitude - The longitude coordinate
 * @returns true if coordinates are valid, false otherwise
 */
export const hasValidCoordinates = (
  latitude: number | null | undefined,
  longitude: number | null | undefined
): boolean => {
  return (
    latitude !== null &&
    latitude !== undefined &&
    longitude !== null &&
    longitude !== undefined &&
    latitude !== 0 &&
    longitude !== 0
  );
};

/**
 * Gets the resolved address from either the object itself or from the store
 *
 * @param objectAddress - The address from the object (can be null/undefined)
 * @param storeAddresses - Array of resolved addresses from the store
 * @param latitude - The latitude to match
 * @param longitude - The longitude to match
 * @returns The resolved address string or undefined
 */
export const getResolvedAddressFromStore = (
  objectAddress: string | null | undefined,
  storeAddresses: any[],
  latitude: number | null | undefined,
  longitude: number | null | undefined
): string | null | undefined => {
  // If object already has an address, use it
  if (objectAddress) {
    return objectAddress;
  }

  // Otherwise, look for it in the store
  const resolvedFromStore = storeAddresses.find(
    (addr) => addr.latitude === latitude && addr.longitude === longitude
  );

  return resolvedFromStore?.address;
};
